import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { UserService } from './user.service';
import { LayerQcService } from './layer-qc.service';
import { AppLayer } from './layer.service';
import { CommonFunctionService } from './common-function.service';
import { ActivityService } from './activity.service';
import { QaNotes, SchemaNote } from 'app/components/content-layers/layer-complex/layer-complex.component';
import { AttrIndexService } from './attr-index.service';
import { CommonTableService, TableColumn } from './common-table.service';
import { Feature } from 'ol';
import { AppinfoService } from './appinfo.service';
import { CommonModalService } from './common-modal.service';
import { StoredRecord } from 'app/models/data-index.model';

// Schema defined by which organization
export type SchemaDefinedByKey = 'standard' | 'state' | 'local';
export interface SchemaDefinedBy {
	name: string;
	value: SchemaDefinedByKey;
	columnsMappingByLayer: {
		[layerId: number]: any
	};
}

export type ContentMenuLayerItemKey = 'live' | 'published' | 'report'; // 'myLayers' | 'collaborative';
export type ContentMenuItemKey = 'home' | ContentMenuLayerItemKey ;
// export type ViewMenuItemKey = 'map' | 'attr';
export type ModelMenuItemKey = 'L' | 'N' | '';
export interface SchemaData {
	currentDefinedBy: SchemaDefinedByKey
}

export interface LabelCalc {
	layerId?;
	ext?: number[];
	minDist?: number;
	pt1?: number[];
	pt2?: number[];
	// minDistByZoom?: {
	// 	[zoom: number]: number; // zoom level integer
	// };
	// zoom?: number;
	// minDistByTile: {
	// 	[tileCoord: string]: number
	// }
}

export interface FilterOption {
	name: string;
	value: string; // code
	descriptionLong?: string;
	errorLevel: 'CRITICAL' | 'WARNING' | 'INFO';
	count?: number;
	isSelected?: boolean;
	type: string; // 'FEATURE' | 'LAYER' | 'SCHEMA' | 'DOMAIN' | 'FISHBONE'
	exceptionAllowed?: boolean;
}
export interface QcFilterInfo {
	// isQcFilter?: boolean;
	isQcUserPrefer?: boolean;
	isCheckAll?: boolean;
	reportQuery?: {
		sql: string;
		cols?: string;
		pkey?: string;
	};
	//
	filterOptions: FilterOption[];
	skipGetData?: boolean;
	source?: 'filterTab' | 'code'
}

@Injectable({
	providedIn: 'root'
})
export class MainDataService {

	// qcFilterOptions: BehaviorSubject<{
	// 	feat: FilterOption[];
	// 	schema: FilterOption[];
	// 	layer: FilterOption[];
	// }> = new BehaviorSubject({
	// 	feat: null,
	// 	schema: null,
	// 	layer: null
	// });
	qcFilterInfo: BehaviorSubject<QcFilterInfo> = new BehaviorSubject({
		// isQcFilter: true,
		isCheckAll: false,
		filterOptions: []
	});
	schemaFilterInfo: BehaviorSubject<{
		filterOptions: FilterOption[];
		source?: 'filterTab';
	}> = new BehaviorSubject({
		filterOptions: []
	});
	schemaDataBS: BehaviorSubject<{
		layerId;
		data: any[]
	}> = new BehaviorSubject({
		layerId: null,
		data: []
	});

	// TODO
	layerFilterInfo: BehaviorSubject<{
		filterOptions: FilterOption[];
		source?: 'filterTab';
	}> = new BehaviorSubject({
		filterOptions: []
	});

	fishboneFilterInfo: BehaviorSubject<{
		allFilterOptions: FilterOption[];
		selectedLayerFilterOptions: FilterOption[];
		isCheckAll?: boolean;
		source?: 'filterTab'
	}> = new BehaviorSubject({
		allFilterOptions: [],
		selectedLayerFilterOptions: []
	});
	fishboneToggleBS: BehaviorSubject<{isOn: boolean;}> = new BehaviorSubject({
		isOn: false
	});

	// Schema columns
	schemas: {
		layerId: any;
		cols: {
			[key in SchemaDefinedByKey]: TableColumn[];
		};
	};


	contentMenuItemKeyBS: BehaviorSubject<ContentMenuItemKey> = new BehaviorSubject(null);
	schemasDefinedByBS: BehaviorSubject<{
		[content in ContentMenuLayerItemKey]: SchemaData
	}> = new BehaviorSubject({
		live: {
			currentDefinedBy: (<SchemaDefinedByKey>'standard')
		},
		published: {
			currentDefinedBy: (<SchemaDefinedByKey>'standard')
		},
		report: {
			currentDefinedBy: (<SchemaDefinedByKey>'standard')
		}
	});
	modelBS: BehaviorSubject<ModelMenuItemKey> = new BehaviorSubject((<ModelMenuItemKey>''));
	modelOptionBS: BehaviorSubject<{
		hasAllFields: boolean
	}> = new BehaviorSubject({
		hasAllFields: true
	});

	editRecordsSubject: Subject<{
		ids: any[]
	}> = new Subject();
	dataEditableBS: BehaviorSubject<{editable?: boolean}> = new BehaviorSubject({});

	addressManageHintBS: BehaviorSubject<{
		action?: 'single' | 'multi';
		message?: string;
	}> = new BehaviorSubject(null)


	// rows: any[]; // store rows in the table
	currentColumns: TableColumn[];

	// Store the whole data (not sample) with all attributes within the extent within the !!!ATTRIBUTE FILTERS!!!
	// can be used when zoom in with map filter on
	// It's only stored when isStoredDataSample is false, but isDisplaySample could be true

	private dataIndex: {
		[id: string]: StoredRecord
	} = {};
	dataIndexObjSubject: Subject<any> = new Subject();

	retrievedData: {
		extent?: number[];
		ids: any[];
	};

		// wholeData: {
		// 	feats?: Feature[];
		// 	rows?: any[];
		// 	extent?: number[];
		// } = {};
	// Store the whole bg data within map filter // NO attribute filter applied
	wholeBgData: {
		surroundingFeats?: Feature[];
		extent?: number[];
		layerId?;
	} = {};
	// Store the whole bg reference data within map filter // NO attribute filter applied
	wholeRefData: {
		feats?: Feature[];
		extent?: number[];
		layerId?;
	} = {};
	wholeBgBdryData: {
		topoFeats?: Feature[];
		extent?: number[];
		layerIds?: number[];
	} = {};
	// Address data label calc
	dataLabelCalc: LabelCalc = {};
	refDataLabelCalc: LabelCalc = {};

	constructor(
		private commonService: CommonFunctionService,
		private activityService: ActivityService,
		private appinfoService: AppinfoService,
		private attrIndexService: AttrIndexService,
		private qcService: LayerQcService,
		private modalService: CommonModalService,
		private tableService: CommonTableService,
		private userService: UserService
	) { }

	async getFilterOptions(layerId: number, isSelected?: boolean) {
		try {
			const data = await this.qcService.getQaRules(this.userService.selectedCustomer.getValue().id, null, layerId);
			const featOptions: FilterOption[] = [];
			// const schemaOptions: FilterOption[] = [];
			// const layerOptions: FilterOption[] = [];;
			data.forEach((d) => {
				const obj: FilterOption = {
					name: d.ruleName,
					value: d.code,
					count: d.count,
					descriptionLong: d.describLong,
					errorLevel: d.errorLevel,
					isSelected: isSelected,
					type: d.type,
					exceptionAllowed: d.exceptionAllowed
				};
				// if (d.scope === 'INTER-LAYER') { return; }
				// if (d.type === 'FEATURE') {
				// 	featOptions.push(obj);
				// } else if (d.type === 'SCHEMA' ) {
				// 	schemaOptions.push(obj);
				// } else if (d.type === 'LAYER') {
				// 	layerOptions.push(obj);
				// } else {
					featOptions.push(obj);
				// }
			});
			return {
				feature: featOptions
				// schema: schemaOptions,
				// layer: layerOptions
			}
		} catch (err) {
			console.log(err);
		}
	}
	async getFilterOptions_fishbone(layerType) {
		const cusId = this.userService.selectedCustomer.getValue().id;
		const fishboneFilterInfo = this.fishboneFilterInfo.getValue();
		let selectedLayerFilterOptions: FilterOption[] = [];
		let allFilterOptions = fishboneFilterInfo.allFilterOptions;
		if (cusId) {
			if (!allFilterOptions.length) {
				const all: FilterOption[] = [];
				const allQaRules = await this.qcService.getQaRules(cusId, null, null, null, null, 'code');
				allQaRules.forEach((d) => {
					const obj = {
						name: d.ruleName,
						value: d.code,
						// description: d.describShort,
						descriptionLong: d.describLong,
						errorLevel: d.errorLevel,
						isSelected: d.code !== 'FS1' && d.code !== 'FS3', // Default set fs1/fs3 unselected since they are not errors
						type: d.type
					};
					all.push(obj);
				});
				allFilterOptions = all;
			}
			selectedLayerFilterOptions = allFilterOptions.filter(o => o.type === layerType);
		}
		return selectedLayerFilterOptions
	}
	getQaCodesFromFilterOptions() {
		// Get qaCodes
		const qaCodes: string[] = [];
		this.qcFilterInfo.getValue().filterOptions.forEach((o) => {
			if (o.isSelected) {
				qaCodes.push(o.value);
			}
		});
		return qaCodes;
	}
	// Get all schema data
	async getSchemaData() {
		const layer = this.activityService.selectedLayerBS.getValue().layer;
		if (!layer) {
			return;
		}
		const randomId = this.commonService.getRandomId();
		this.commonService.setLoadingSpinner(true, randomId);
		let rsp;
		try {		
			rsp = await this.qcService.getQcSchemaReport(layer.id);
		} catch (err) {
			console.log(err);
		}
		this.commonService.setLoadingSpinner(false, randomId);

		this.schemaDataBS.next({
			layerId: layer.id,
			data: rsp.data
		});
		return rsp;
	}
	getTooltipFromQaNotes(notes: QaNotes, col: string) {
		let tooltip = '';
		if (notes && notes[col]) {
			notes[col].forEach((i, idx) => {
				if (i.msg) {
					tooltip += i.msg + (idx === notes[col].length - 1 ? '' : '\n');
				}
			});
		}
		return tooltip;
	}
	getTooltipFromSchemaNotes(notes: SchemaNote[]) {
		let tooltip = '';
		if (notes) {
			notes.forEach((i, idx) => {
				if (i.msg) {
					tooltip += i.msg + (idx === notes.length - 1 ? '' : '\n');
				}
			});
		}
		return tooltip;
	}


	getSchemaDefinedBy(): SchemaDefinedBy[] {
		return [
			{
				name: 'NENA',
				value: 'standard',
				columnsMappingByLayer: {}
			}, {
				name: 'LOCAL',
				value: 'local',
				columnsMappingByLayer: {}
			}, {
				name: 'STATE',
				value: 'state',
				columnsMappingByLayer: {}
			}
		];
	}

	// Transform rows to extract data of attr columns in nstd/state columns
	transformJsonData(type: 'nstd' | 'state', rows, schemaDefinedBys: SchemaDefinedBy[], isAddColumns?: boolean, layerId?) {
		const endWithStr = type === 'nstd' ? this.attrIndexService.jsonbColumnEnd : this.attrIndexService.jsonbColumnEnd_state;
		// Get the column that stores nstd
		const keyCol = Object.keys(rows[0]).find((col) => {
			return col.endsWith(endWithStr);
		});
		const colsByProp = {};
		if (keyCol) {
			rows.forEach((r) => {
				if (r[keyCol]) {
					for (const attr of Object.keys(r[keyCol])) {
						r[attr] = r[keyCol][attr];
						if (!colsByProp[attr]) {
							colsByProp[attr] = this.tableService.setNewColumn(attr);
						}
					}
				}
			});
			// Add attr columns to local schema
			if (isAddColumns) {
				let targetSchema: SchemaDefinedBy;
				switch (type) {
					case 'nstd': targetSchema = schemaDefinedBys.find(d => d.value === 'local'); break;
					case 'state': targetSchema = schemaDefinedBys.find(d => d.value === 'state');
				}
				let columns = targetSchema.columnsMappingByLayer[layerId];
				columns = columns.concat(Object.keys(colsByProp).map((k) => {
					return colsByProp[k];
				}));
				targetSchema.columnsMappingByLayer[layerId] = columns;
			}
		}
	}

 	// Transform rows to extract data of attr columns in nstd/state columns
	unpackJsonbData_myLayers(type: 'nstd' | 'state', rows, schemas: {[key in SchemaDefinedByKey]: TableColumn[];}, isAddColumns?: boolean) {
		const endWithStr = type === 'nstd' ? this.attrIndexService.jsonbColumnEnd : this.attrIndexService.jsonbColumnEnd_state;
		// Get the column that stores nstd
		const keyCol = Object.keys(rows[0]).find((col) => {
			return col.endsWith(endWithStr);
		});
		if (keyCol) {
			const colsByProp = this.unpackJsonbIntoRows(rows, keyCol);
			// Add attr columns to local schema
			if (isAddColumns) {
				const columns = type === 'state' ? schemas.state : schemas.local;
				Object.keys(colsByProp).map((k) => {
					return colsByProp[k];
				}).forEach(c => columns.push(c));
			}
		}
	}
	unpackJsonbData_collaborative(rows: any[], columns: TableColumn[]) {
		if (!rows || !rows.length) {
			return;
		}
		const endWithStr = this.attrIndexService.jsonbColumnEnd;
		// Get the column that stores nstd
		const keyCol = Object.keys(rows[0]).find((col) => {
			return col.endsWith(endWithStr);
		});
		if (keyCol) {
			const colsByProp = this.unpackJsonbIntoRows(rows, keyCol);
			if (!Object.keys(colsByProp).length) {
				return;
			}

			const jsonbColIdx = columns.findIndex(c => c.prop.endsWith(endWithStr));
			// If jsonbCol hasn't been removed from columns, it means it's the first time to unpack jsonb
			// In this case, remove jsonb col and add unpacked cols
			if (jsonbColIdx > -1) {
				// remove
				columns.splice(jsonbColIdx, 1);
				// Add attr columns to local schema
				Object.keys(colsByProp).map((k) => {
					return colsByProp[k];
				}).forEach(c => columns.push(c));
			}
		}
	}
	unpackJsonbIntoRows(rows: any[], jsonbCol: string) {
		// columns unpacked from jsonb
		const colsByProp: {
			[prop: string]: TableColumn
		} = {};
		rows.forEach((r) => {
			if (r[jsonbCol]) {
				for (const attr of Object.keys(r[jsonbCol])) {
					r[attr] = r[jsonbCol][attr];
					if (!colsByProp[attr]) {
						colsByProp[attr] = this.tableService.setNewColumn(attr);
						colsByProp[attr].info.isJsonbCol = true;
						// colsByProp[attr].info.jsonbType = type;
					}
				}
			}
		});
		return colsByProp;
	}


	// Add card to editCardList
	// Use this OUTSIDE edit-card component (inside edit-card component please use addCardToList)
	addRecToCard(recId, layerCateCode?: string, source?, isDelete?: boolean, isHold?: boolean) {
		const attrs = this.getDataAttrs(recId);
		if (!attrs[this.attrIndexService.isEditedKey]) { //  && !attrs[this.attrIndexService.isDeletedKey]
			if (isDelete) {
				this.setDataAttrs(recId, this.attrIndexService.isDeletedKey, true);
			} else {
				this.setDataAttrs(recId, this.attrIndexService.isEditedKey, true);
			}
			// For provBdry, check discrepancyId
			if (layerCateCode && layerCateCode === this.attrIndexService.cateCodeProvBdry) {
				const prefix = this.appinfoService.layerTypeList.find(t => t.code === layerCateCode).colPrefix;
				const discrepancyAgencyIdColName = prefix + this.attrIndexService.discrepancyAgencyIdStandardLower;
				if (attrs[discrepancyAgencyIdColName] !== this.appinfoService.customerProfile.discrepancyAgencyId) {
					const hiddenIds = this.activityService.editRecordListHiddenBS.getValue().ids;
					hiddenIds.push(recId);
					this.activityService.editRecordListHiddenBS.next({
						ids: hiddenIds,
						source: 'main'
					});
					return;
				}
			}
			if (!isHold) {
				const editedIds = this.activityService.editRecordListBS.getValue().ids;
				if (editedIds.indexOf(recId) === -1) {
					editedIds.push(recId);
					this.activityService.editRecordListBS.next({
						ids: editedIds,
						source: source || 'main'
					});
				}
			} else {
				const editedIds = this.activityService.editRecordHoldListBS.getValue().ids;
				if (editedIds.indexOf(recId) === -1) {
					editedIds.push(recId);
					this.activityService.editRecordHoldListBS.next({
						ids: editedIds,
						source: source
					});
				}
			}
		}
	}
	addRecToHiddenList(recId) {
		const attrs = this.getDataAttrs(recId);
		// if (!attrs[this.attrIndexService.isEditedKey]) {
			// this.setDataAttrs(recId, this.attrIndexService.isEditedKey, true);
			const hiddenIds = this.activityService.editRecordListHiddenBS.getValue().ids;
			if (hiddenIds.indexOf(recId) === -1) {
				hiddenIds.push(recId);
				this.activityService.editRecordListHiddenBS.next({
					ids: hiddenIds
				});
			}
		// }
		return attrs;
	}
	// Add NEW record to card & table (no geom added)
	addNewCard(idCol: string, source?, isHold?: boolean) {
		const rec = {};
		rec[idCol] = this.commonService.getRandomId();
		rec[this.attrIndexService.isNewDataKey] = true;
		rec[this.attrIndexService.isEditedKey] = true;

		this.addDataToStoredRecords([rec], idCol, 'replace');

		if (!isHold) {
			const editedIds = this.activityService.editRecordListBS.getValue().ids;
			editedIds.push(rec[idCol]);
			this.activityService.editRecordListBS.next({
				ids: editedIds,
				source: source || 'main'
			});
		} else {
			const editedIds = this.activityService.editRecordHoldListBS.getValue().ids;
			editedIds.push(rec[idCol]);
			this.activityService.editRecordHoldListBS.next({
				ids: editedIds,
				source: source || 'main'
			});
		}

		return rec;
	}
	removeCard(id) {
		const editedIds = this.activityService.editRecordListBS.getValue().ids;
		const idx = editedIds.findIndex(i => i === id);
		if (idx > -1) {
			editedIds.splice(idx, 1);
			this.activityService.editRecordListBS.next({
				ids: editedIds
			});
		}
	}

	// data should be original data
	// For parameter handlePrev: 
	//    'replace' means the data will completely replace what was in dataIndex; 
	//    'mergePrev' means keep both but properties in dataIndex will have higher priority; 
	//    'prev' means if dataIndex already has it, use dataIndex and ignore data 
	addDataToStoredRecords(data: any[], idCol: string, handlePrev?: 'replace' | 'mergeToPrev' | 'prev') {
		if (!data) {
			return;
		}
		handlePrev = handlePrev || 'mergeToPrev';
		data.forEach(i => {
			switch (handlePrev) {
				case 'replace':
					this.dataIndex[i[idCol]] = {
						attrs: i,
						originalAttrs: JSON.parse(JSON.stringify(i))
					};
					break;
				case 'mergeToPrev':
					if (this.dataIndex[i[idCol]]) {
						this.dataIndex[i[idCol]].originalAttrs = JSON.parse(JSON.stringify(i));
						this.dataIndex[i[idCol]].attrs = Object.assign(i, this.dataIndex[i[idCol]].attrs);
					} else {
						this.dataIndex[i[idCol]] = {
							attrs: i,
							originalAttrs: JSON.parse(JSON.stringify(i))
						};	
					}
					break;
				case 'prev':
					if (this.dataIndex[i[idCol]]) {
						return;
					}
					this.dataIndex[i[idCol]] = {
						attrs: i,
						originalAttrs: JSON.parse(JSON.stringify(i))
					};
					break;
			}
		})
	}
	removeDataFromStoredRecords(id) {
		delete this.dataIndex[id];
	}
	replaceDataIndex(dataIndex: {
		[id: string]: StoredRecord
	}) {
		this.dataIndex = dataIndex;
		this.dataIndexObjSubject.next();
	}
	getDataAttrs(id) {
		return this.dataIndex[id] ? this.dataIndex[id].attrs : null;
	}
	getDataOriginAttrs(id) {
		return this.dataIndex[id] ? this.dataIndex[id].originalAttrs : null;
	}
	setDataAttrs(id, prop, val) {
		if (!this.dataIndex[id]) {
			return;
		}
		this.dataIndex[id].attrs[prop] = val;
	}
	setDataOriginAttrs(id, prop, val) {
		if (!this.dataIndex[id]) {
			return;
		}
		this.dataIndex[id].originalAttrs[prop] = val;
	}
	clearDataChangeInIndex(id) {
		const origin = this.getDataOriginAttrs(id);
		if (!origin) { // if it's new card, discard it
			return;
		}
		const attrs = this.getDataAttrs(id);
		Object.keys(attrs).forEach(key => {
			if (origin[key] && typeof origin[key] === 'object') {
				attrs[key] = JSON.parse(JSON.stringify(origin[key]));
			} else {
				attrs[key] = origin[key];
			}
		});
	}
	getDataIndex() {
		return this.dataIndex;
	}
}
